import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useMessage } from "../i18n/locale";
import {
  getCurrentLocation,
  getLocationCities,
  searchLocationCities,
} from "../services/location";

const LIVE_SEARCH_DELAY_MS = 1000;

function normalizeSearchValue(value) {
  return String(value || "").trim().normalize("NFKC").toLocaleLowerCase();
}

function cityMatches(city, query) {
  const normalizedQuery = normalizeSearchValue(query);
  return [city.name, city.displayName].some((value) =>
    normalizeSearchValue(value).includes(normalizedQuery)
  );
}

function canonicalCityName(city) {
  const source = String(city?.name || city?.displayName || "");
  return source.split(",", 1)[0].trim();
}

function distanceKm(city, location) {
  const cityLatitude = Number(city.latitude);
  const cityLongitude = Number(city.longitude);
  const originLatitude = Number(location?.latitude);
  const originLongitude = Number(location?.longitude);
  if (
    !Number.isFinite(cityLatitude)
    || !Number.isFinite(cityLongitude)
    || !Number.isFinite(originLatitude)
    || !Number.isFinite(originLongitude)
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const latitudeDelta = toRadians(cityLatitude - originLatitude);
  const longitudeDelta = toRadians(cityLongitude - originLongitude);
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(toRadians(originLatitude))
    * Math.cos(toRadians(cityLatitude))
    * Math.sin(longitudeDelta / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function rankCityOptions(options, query, location) {
  const normalizedQuery = normalizeSearchValue(query);
  return options
    .filter((city) => !normalizedQuery || cityMatches(city, normalizedQuery))
    .map((city, index) => ({ city, index, distance: distanceKm(city, location) }))
    .sort((left, right) => {
      if (Number.isFinite(left.distance) !== Number.isFinite(right.distance)) {
        return Number.isFinite(left.distance) ? -1 : 1;
      }
      const distanceDifference = left.distance - right.distance;
      if (Number.isFinite(distanceDifference) && distanceDifference !== 0) {
        return distanceDifference;
      }

      const leftName = normalizeSearchValue(left.city.name);
      const rightName = normalizeSearchValue(right.city.name);
      const leftRelevance = leftName === normalizedQuery ? 0 : leftName.startsWith(normalizedQuery) ? 1 : 2;
      const rightRelevance = rightName === normalizedQuery ? 0 : rightName.startsWith(normalizedQuery) ? 1 : 2;
      return leftRelevance - rightRelevance || left.index - right.index;
    })
    .map(({ city }) => city);
}

export default function CitySelect({
  label,
  value = "",
  required = false,
  onChange,
  autoComplete = "address-level2",
  name = "city",
  onKeyDown,
  ...textFieldProps
}) {
  const defaultLabel = useMessage("cityFieldLabel");
  const selectPrompt = useMessage("cityFieldPrompt");
  const errorLabel = useMessage("cityFieldError");
  const attribution = useMessage("cityFieldAttribution");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activatedQuery, setActivatedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const latestSearchRef = useRef(0);
  const lastAutomaticQueryRef = useRef("");
  const locationRequestedRef = useRef(false);
  const lastCommittedCityRef = useRef("");

  useEffect(() => {
    let active = true;
    getLocationCities()
      .then((items) => {
        if (active) setCities(items);
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedCity = cities.find((city) => city.name === value)
    || (value ? { id: `legacy-${value}`, name: value, displayName: value } : null);
  const rankedCities = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(searchText);
    if (normalizedQuery && activatedQuery !== normalizedQuery) return [];
    return rankCityOptions(cities, searchText, currentLocation);
  }, [activatedQuery, cities, currentLocation, searchText]);

  const requestCurrentLocation = useCallback(() => {
    if (locationRequestedRef.current) return;
    locationRequestedRef.current = true;
    getCurrentLocation()
      .then(setCurrentLocation)
      .catch(() => {
        // Text relevance remains the fallback when location is unavailable or denied.
      });
  }, []);

  const runWorldwideSearch = useCallback(async (query = searchText) => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) return;
    setActivatedQuery(normalizeSearchValue(normalizedQuery));
    const searchId = ++latestSearchRef.current;
    requestCurrentLocation();
    setLoading(true);
    setLoadError(false);
    try {
      const results = await searchLocationCities(normalizedQuery);
      if (searchId !== latestSearchRef.current) return;
      setCities((current) => [
        ...current,
        ...results.filter((result) => !current.some((city) => city.id === result.id)),
      ]);
      setOpen(true);
    } catch {
      if (searchId === latestSearchRef.current) setLoadError(true);
    } finally {
      if (searchId === latestSearchRef.current) setLoading(false);
    }
  }, [requestCurrentLocation, searchText]);

  const handleSearchKeyDown = useCallback((event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.key !== "Enter" || event.nativeEvent?.isComposing) return;

    const query = searchText.trim();
    const normalizedQuery = normalizeSearchValue(query);
    const hasExactCity = cities.some(
      (city) => normalizeSearchValue(city.name) === normalizedQuery
    );
    if (query.length < 2 || hasExactCity || lastAutomaticQueryRef.current === normalizedQuery) return;

    event.preventDefault();
    lastAutomaticQueryRef.current = normalizedQuery;
    setActivatedQuery(normalizedQuery);
    runWorldwideSearch(query);
  }, [cities, onKeyDown, runWorldwideSearch, searchText]);

  const commitCitySelection = useCallback((city) => {
    const cityName = canonicalCityName(city);
    const selectionKey = city?.id || cityName;
    setSearchText(cityName);
    setActivatedQuery(normalizeSearchValue(cityName));
    if (selectionKey && lastCommittedCityRef.current === selectionKey) return;
    lastCommittedCityRef.current = selectionKey;
    onChange?.({
      target: { name, value: cityName },
      city: city || null,
    });
  }, [name, onChange]);

  useEffect(() => {
    const normalizedInput = normalizeSearchValue(searchText);
    if (!normalizedInput) return;
    const exactCity = cities.find((city) =>
      [city.name, city.displayName].some(
        (candidate) => normalizeSearchValue(candidate) === normalizedInput
      )
    );
    if (exactCity && exactCity.name !== value) {
      commitCitySelection(exactCity);
    }
  }, [cities, commitCitySelection, searchText, value]);

  useEffect(() => {
    const normalizedQuery = searchText.trim();
    if (
      normalizedQuery.length < 2
      || loading
      || lastAutomaticQueryRef.current === normalizeSearchValue(normalizedQuery)
      || cities.some((city) => normalizeSearchValue(city.name) === normalizeSearchValue(normalizedQuery))
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const normalizedSearch = normalizeSearchValue(normalizedQuery);
      if (lastAutomaticQueryRef.current === normalizedSearch) return;
      lastAutomaticQueryRef.current = normalizedSearch;
      setActivatedQuery(normalizedSearch);
      runWorldwideSearch(normalizedQuery);
    }, LIVE_SEARCH_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [cities, loading, runWorldwideSearch, searchText]);

  return (
    <Box>
      <Autocomplete
        options={rankedCities}
        filterOptions={(options) => options}
        open={open}
        onOpen={() => {
          setOpen(true);
          requestCurrentLocation();
        }}
        onClose={() => setOpen(false)}
        value={selectedCity}
        inputValue={searchText || selectedCity?.name || ""}
        loading={loading}
        disabled={textFieldProps.disabled}
        getOptionLabel={(city) => city.displayName || city.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onInputChange={(_, nextText, reason) => {
          if (reason === "input" || reason === "clear") {
            lastCommittedCityRef.current = "";
            setSearchText(nextText);
            setActivatedQuery("");
          }
        }}
        onChange={(_, city) => commitCitySelection(city)}
        noOptionsText={selectPrompt}
        renderOption={(props, city) => {
          const { key, onClick: selectOption, ...optionProps } = props;
          return (
            <li
              key={key}
              {...optionProps}
              onClick={(event) => {
                selectOption?.(event);
                commitCitySelection(city);
              }}
            >
              {city.displayName || city.name}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...textFieldProps}
            {...params}
            inputProps={{
              ...params.inputProps,
              name,
              autoComplete,
            }}
            required={required}
            label={label || defaultLabel}
            helperText={loadError ? errorLabel : textFieldProps.helperText}
            onKeyDown={handleSearchKeyDown}
          />
        )}
      />
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        {attribution}
      </Typography>
    </Box>
  );
}
